// Bao bọc @vladmandic/face-api. Thư viện + model nặng (TF.js) nên LAZY-LOAD bằng import()
// -> chỉ tải khi nhân viên thực sự mở màn chấm công, không phình bundle chính.
// Model nhúng offline ở /models (không cần internet).

const MODEL_URL = `${import.meta.env.BASE_URL || '/'}models`.replace(/\/+/g, '/');
let _fa = null;
let loadingDet = null;
let loadingRec = null;
let _backend = null; // backend TF.js đang dùng ('webgl' | 'cpu' | ...) sau khi khởi tạo

// Kiểm tra NHANH (đồng bộ, không cần tải face-api) khả năng WebGL của máy:
// - ok: có ngữ cảnh WebGL không.
// - software: WebGL chạy PHẦN MỀM (SwiftShader/llvmpipe/Microsoft Basic) — GPU bị chặn/tắt tăng tốc
//   phần cứng. Trường hợp này TF.js chạy trên CPU đồng bộ -> cực chậm -> treo "Trang không phản hồi".
export function webglInfo() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl');
    if (!gl) return { ok: false, software: false, renderer: '' };
    let renderer = '';
    try {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext) renderer = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '');
    } catch { /* trình duyệt có thể ẩn tên GPU */ }
    const software = /swiftshader|software|llvmpipe|basic render|microsoft basic|mesa offscreen/i.test(renderer);
    return { ok: true, software, renderer };
  } catch {
    return { ok: false, software: false, renderer: '' };
  }
}

// Tương thích chỗ gọi cũ: chỉ hỏi "có WebGL không".
export function webglSupported() {
  return webglInfo().ok;
}

export function faceBackend() {
  return _backend;
}

async function fa() {
  if (!_fa) {
    const m = await import('@vladmandic/face-api');
    // Ép backend WebGL (GPU) — nếu để face-api tự chọn mà WebGL lỗi, nó rơi về CPU thuần JS
    // chạy đồng bộ -> TREO main thread ("Trang không phản hồi"). WebGL đẩy tính toán sang GPU.
    try { await m.tf.setBackend('webgl'); await m.tf.ready(); } catch { /* không có WebGL: để mặc định */ }
    _backend = m.tf.getBackend();
    _fa = m;
  }
  return _fa;
}

// Detector + landmark (~540KB): đủ cho XEM TRƯỚC + liveness. Nhẹ -> mở màn nhanh.
export function loadDetectors() {
  if (!loadingDet) {
    loadingDet = (async () => {
      const faceapi = await fa();
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      ]);
    })().catch((e) => { loadingDet = null; throw e; });
  }
  return loadingDet;
}

// Recognition net (~6MB): chỉ cần khi TRÍCH VECTOR (đăng ký / chốt chấm công). Tải nền, lười.
export function loadRecognition() {
  if (!loadingRec) {
    loadingRec = (async () => {
      const faceapi = await fa();
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
    })().catch((e) => { loadingRec = null; throw e; });
  }
  return loadingRec;
}

// Tải cả 3 model (giữ để tương thích chỗ gọi cũ).
export function loadModels() {
  return Promise.all([loadDetectors(), loadRecognition()]);
}

// Eye Aspect Ratio: nhỏ khi nhắm mắt (~<0.2), lớn khi mở (~0.3).
function ear(eye) {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return (d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * d(eye[0], eye[3]));
}

// Lấy MẪU NHANH cho liveness (chỉ detector + landmark): độ mở mắt (ear) + độ lệch đầu ngang (yaw).
// yaw = vị trí mũi lệch so với tâm khung mặt, chuẩn hoá theo bề rộng mặt (~ -0.5..0.5).
export async function detectLive(video) {
  const faceapi = await fa();
  await loadDetectors();
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });
  const r = await faceapi.detectSingleFace(video, opts).withFaceLandmarks();
  if (!r) return null;
  const lm = r.landmarks;
  const e = (ear(lm.getLeftEye()) + ear(lm.getRightEye())) / 2;
  const box = r.detection.box;
  const nose = lm.getNose();
  const tip = nose[nose.length - 1] || nose[3];
  const yaw = box.width ? (tip.x - (box.x + box.width / 2)) / box.width : 0;
  return { ear: e, yaw };
}

// Trích vector 128 số (dùng khi đăng ký & lúc chốt chấm công). null nếu không thấy mặt.
export async function getDescriptor(video) {
  const faceapi = await fa();
  await loadRecognition(); // đảm bảo model nặng đã tải xong trước khi trích vector
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });
  const r = await faceapi.detectSingleFace(video, opts).withFaceLandmarks().withFaceDescriptor();
  return r ? Array.from(r.descriptor) : null;
}
